import {
  type Filter,
  type FilterId,
  FilterType,
  type FreeTextFilter,
  isMultiFilter,
  type ListFilter,
  type PrimarySelection,
  type StaticListFilter,
  type TreeFilter,
} from '../types/filters';

const isNegatedInstance = (filter: Filter): boolean =>
  'isNot' in filter && Boolean(filter.isNot);

const dedupeByKey = <T>(items: T[], getKey: (item: T) => string): T[] => {
  const seen = new Set<string>();
  const result: T[] = [];

  for (const item of items) {
    const key = getKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }

  return result;
};

const mergeSelectionListIds = (
  instances: TreeFilter[]
): TreeFilter['selectionListIds'] => {
  const ids = new Set<TreeFilter['selectionListIds'][number]>();
  for (const instance of instances) {
    for (const id of instance.selectionListIds) {
      ids.add(id);
    }
  }
  return Array.from(ids);
};

const mergePrimarySelections = (
  instances: TreeFilter[]
): PrimarySelection[] => {
  const merged = new Map<string, PrimarySelection>();

  for (const instance of instances) {
    const primarySelections = instance.primarySelections ?? [];
    for (const selection of primarySelections) {
      const key = `${selection.levelId}:${selection.id}`;
      const existing = merged.get(key);
      if (!existing || (!existing.isActive && selection.isActive)) {
        merged.set(key, selection);
      }
    }
  }

  return Array.from(merged.values());
};

const mergeTreeInstances = (
  wrappedFilterId: FilterId,
  instances: TreeFilter[]
): TreeFilter | null => {
  if (instances.length === 0) return null;

  const base = instances[0];
  if (!base) return null;
  const baseFilter: TreeFilter = base;
  const mergedSelections = dedupeByKey(
    instances.flatMap((instance) => instance.selections ?? []),
    (selection) => `${selection.levelId}:${selection.id}`
  );
  const mergedPrimarySelections = mergePrimarySelections(instances);

  return {
    ...baseFilter,
    id: wrappedFilterId,
    type: FilterType.TREE,
    selectionListIds: mergeSelectionListIds(instances),
    selections: mergedSelections,
    ...(mergedPrimarySelections.length > 0
      ? { primarySelections: mergedPrimarySelections }
      : {}),
  };
};

const mergeListInstances = (
  wrappedFilterId: FilterId,
  instances: ListFilter[]
): ListFilter | null => {
  if (instances.length === 0) return null;

  const base = instances[0];
  if (!base) return null;
  const baseFilter: ListFilter = base;
  const mergedSelections = dedupeByKey(
    instances.flatMap((instance) => instance.selections ?? []),
    (selection) => String(selection.id)
  );

  return {
    ...baseFilter,
    id: wrappedFilterId,
    type: FilterType.LIST,
    selections: mergedSelections,
  };
};

const mergeStaticListInstances = (
  wrappedFilterId: FilterId,
  instances: StaticListFilter[]
): StaticListFilter | null => {
  if (instances.length === 0) return null;

  const base = instances[0];
  if (!base) return null;
  const baseFilter: StaticListFilter = base;
  const mergedSelections = dedupeByKey(
    instances.flatMap((instance) => instance.selections ?? []),
    (selection) => String(selection.id)
  );

  return {
    ...baseFilter,
    id: wrappedFilterId,
    type: FilterType.STATIC_LIST,
    selections: mergedSelections,
  };
};

const mergeFreeTextInstances = (
  wrappedFilterId: FilterId,
  instances: FreeTextFilter[]
): FreeTextFilter | null => {
  if (instances.length === 0) return null;

  const base = instances[0];
  if (!base) return null;
  const baseFilter: FreeTextFilter = base;
  const mergedText = dedupeByKey(
    instances.flatMap((instance) => instance.text ?? []),
    (value) => value
  );

  return {
    ...baseFilter,
    id: wrappedFilterId,
    type: FilterType.FREE_TEXT,
    text: mergedText,
  };
};

export const flattenMultiFiltersForTooltip = (filters: Filter[]): Filter[] => {
  const instancesById = new Map<FilterId, Filter[]>();
  const order: FilterId[] = [];

  const pushInstance = (id: FilterId, instance: Filter) => {
    if (!instancesById.has(id)) {
      instancesById.set(id, []);
      order.push(id);
    }
    const list = instancesById.get(id);
    if (list) {
      list.push(instance);
    }
  };

  for (const filter of filters) {
    if (isMultiFilter(filter)) {
      const instances = filter.instances.filter(
        (instance) => !isNegatedInstance(instance)
      );
      for (const instance of instances) {
        pushInstance(filter.wrappedFilterId, instance);
      }
      continue;
    }

    pushInstance(filter.id, filter);
  }

  const result: Filter[] = [];

  for (const id of order) {
    const instances = instancesById.get(id) ?? [];
    const firstInstance = instances[0];
    if (!firstInstance) continue;

    const baseType = firstInstance.type;
    const sameTypeInstances = instances.filter(
      (instance) => instance.type === baseType
    );

    if (baseType === FilterType.TREE) {
      const merged = mergeTreeInstances(id, sameTypeInstances as TreeFilter[]);
      if (merged) result.push(merged);
      continue;
    }

    if (baseType === FilterType.LIST) {
      const merged = mergeListInstances(id, sameTypeInstances as ListFilter[]);
      if (merged) result.push(merged);
      continue;
    }

    if (baseType === FilterType.STATIC_LIST) {
      const merged = mergeStaticListInstances(
        id,
        sameTypeInstances as StaticListFilter[]
      );
      if (merged) result.push(merged);
      continue;
    }

    if (baseType === FilterType.FREE_TEXT) {
      const merged = mergeFreeTextInstances(
        id,
        sameTypeInstances as FreeTextFilter[]
      );
      if (merged) result.push(merged);
      continue;
    }

    const fallback = sameTypeInstances[0];
    if (fallback) {
      result.push({ ...fallback, id });
    }
  }

  return result;
};
